import type { UploadRawFile } from './upload'

interface InternalDataTransferItem extends DataTransferItem {
  isFile: boolean
  file: (
    cd: (file: UploadRawFile & { webkitRelativePath?: string }) => void
  ) => void
  createReader: () => any
  fullPath: string
  isDirectory: boolean
  name: string
  path: string
}

async function loopFiles(
  item: InternalDataTransferItem
): Promise<InternalDataTransferItem[]> {
  const dirReader = item.createReader()
  let fileList: InternalDataTransferItem[] = []

  let entries: InternalDataTransferItem[] = []
  do {
    entries = await new Promise((resolve) => {
      dirReader.readEntries((entries: InternalDataTransferItem[]) => {
        const entryList = Array.prototype.slice.apply(entries)
        fileList = fileList.concat(entryList)

        resolve(entries)
      })
    })
  } while (!entries.length)

  return fileList
}

export const traverseFileTree = async (
  files: InternalDataTransferItem[],
  callback: (files: File[]) => void
) => {
  const fileList: File[] = []
  const stack: { item: InternalDataTransferItem; path: string }[] = []

  files.forEach((file) => {
    stack.push({ item: file.webkitGetAsEntry() as any, path: '' })
  })

  while (stack.length > 0) {
    const { item, path } = stack.pop()!
    if (!item) {
      continue
    }

    item.path = path

    if (item.isFile) {
      await new Promise<void>((resolve) => {
        item.file((file: File) => {
          if (item.fullPath && !file.webkitRelativePath) {
            Object.defineProperty(file, 'webkitRelativePath', {
              value: item.fullPath.replace(/^\//, ''),
              writable: false,
            })
          }
          fileList.push(file)
          resolve()
        })
      })
    } else if (item.isDirectory) {
      const entries: InternalDataTransferItem[] = await loopFiles(item)
      entries.forEach((entryItem) => {
        stack.push({ item: entryItem, path: `${path}${item.name}/` })
      })
    }
  }

  callback(fileList)
}

export const checkAccept = (file: File, accept: string) => {
  const { type, name } = file
  const extension = name.includes('.') ? `.${name.split('.').pop()}` : ''
  const baseType = type.replace(/\/.*$/, '')
  return accept
    .split(',')
    .map((type) => type.trim())
    .filter((type) => type)
    .some((acceptedType) => {
      if (acceptedType.startsWith('.')) {
        return extension === acceptedType
      }
      if (/\/\*$/.test(acceptedType)) {
        return baseType === acceptedType.replace(/\/\*$/, '')
      }
      if (/^[^/]+\/[^/]+$/.test(acceptedType)) {
        return type === acceptedType
      }
      return false
    })
}

export function getFileSizeText(number: number) {
  if (number < 1024) {
    return `${number} Bytes`
  }
  if (number >= 1024 && number < 1048576) {
    return `${(number / 1024).toFixed(1)} KB`
  }
  if (number >= 1048576) {
    return `${(number / 1048576).toFixed(1)} MB`
  }
  return ''
}
